<template>
  <dialog
    ref="dialogEl"
    class="scrim"
    aria-labelledby="last-call-dialog-title"
    @cancel.prevent="emit('close')"
    @click="onScrimClick"
  >
    <div class="panel">
      <div class="hero">
        <img
          class="hero-art"
          src="/assets/mats/last-call.png"
          alt=""
        >
        <div class="hero-shade"></div>
        <CloseXButton
          class="close"
          :label="t('changelog.dismiss')"
          @click="emit('close')"
        />
        <div class="hero-copy">
          <p class="kicker">{{ t('lastCallDialog.kicker') }}</p>
          <h2 id="last-call-dialog-title">{{ t('missions.tabs.lastCall') }}</h2>
          <p class="until">{{ until }}</p>
        </div>
      </div>

      <div class="body">
        <p class="lead">
          <template v-for="(part, i) in leadParts" :key="i">
            <strong v-if="part.em">{{ part.text }}</strong>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>
        <ul class="rewards">
          <li v-for="step in steps" :key="step.key" class="reward">
            <div class="shot" :class="step.key">
              <AvatarView
                v-if="step.key === 'frame'"
                :config="frameLook"
                :size="120"
                :label="t('missions.lastCall.frame')"
              />
              <AvatarView
                v-else-if="step.key === 'portrait'"
                :config="portraitLook"
                :size="120"
                :label="t('missions.lastCall.portrait')"
              />
              <img
                v-else-if="step.key === 'sleeve'"
                src="/assets/sleeves/last-call.png"
                :alt="t('missions.lastCall.sleeve')"
              >
              <img
                v-else-if="step.key === 'mat'"
                class="mat"
                src="/assets/mats/last-call.png"
                :alt="t('missions.lastCall.mat')"
              >
              <div v-else class="pack-shot">
                <img
                  class="pack"
                  src="/assets/packs/lob.jpg"
                  :alt="t('missions.lastCall.pack')"
                >
                <img
                  v-if="showCard"
                  class="card"
                  :src="cardSrc"
                  alt=""
                  @error="showCard = false"
                >
              </div>
            </div>
            <p class="at">{{ step.at }}</p>
            <p class="name">{{ t(`missions.lastCall.${step.key}`) }}</p>
          </li>
        </ul>
        <p class="foil">{{ t('lastCallDialog.foil') }}</p>
        <p class="rules">{{ t('lastCallDialog.rules') }}</p>
        <div class="meter">
          <progress :value="played" :max="lastCall.packAt"></progress>
          <p>
            {{
              t('missions.progress', {
                current: played,
                target: lastCall.packAt,
              })
            }}
          </p>
        </div>
      </div>

      <div class="actions">
        <button type="button" class="ghost snooze" @click="emit('snooze')">
          {{ t('dialog.snoozeToday') }}
        </button>
        <button type="button" class="ghost" @click="emit('close')">
          {{ t('changelog.dismiss') }}
        </button>
        <router-link to="/missions?tab=last-call" class="cta" @click="emit('close')">
          {{ t('home.lastCallOpen') }}
        </router-link>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  LAST_CALL_FRAME_AT,
  LAST_CALL_FRAME_ID,
  LAST_CALL_MAT_AT,
  LAST_CALL_PACK_AT,
  LAST_CALL_PORTRAIT_AT,
  LAST_CALL_PORTRAIT_ID,
  LAST_CALL_SLEEVE_AT,
} from '@shared/index'
import { useI18n } from '../i18n/t'
import { useLastCallStore } from '../stores/lastCall'
import { usePlayerStore } from '../stores/player'
import { getLocalCardImageUrl } from '../utils/cardImages'
import AvatarView from './AvatarView.vue'
import CloseXButton from './CloseXButton.vue'

const emit = defineEmits<{ close: []; snooze: [] }>()
const { t } = useI18n()
const lastCall = useLastCallStore()
const player = usePlayerStore()
const dialogEl = ref<HTMLDialogElement | null>(null)
const showCard = ref(true)
const cardSrc = getLocalCardImageUrl('89631139')

const steps = [
  { key: 'frame', at: LAST_CALL_FRAME_AT },
  { key: 'portrait', at: LAST_CALL_PORTRAIT_AT },
  { key: 'sleeve', at: LAST_CALL_SLEEVE_AT },
  { key: 'mat', at: LAST_CALL_MAT_AT },
  { key: 'pack', at: LAST_CALL_PACK_AT },
] as const

const portraitLook = computed(() => ({
  cardId: LAST_CALL_PORTRAIT_ID,
  frameId: LAST_CALL_FRAME_ID,
}))

const frameLook = computed(() => ({
  ...player.avatar,
  frameId: LAST_CALL_FRAME_ID,
}))

const played = computed(() =>
  Math.min(lastCall.played, lastCall.packAt),
)

const leadParts = computed(() => {
  const raw = t('lastCallDialog.lead')
  const parts: { em: boolean; text: string }[] = []
  const pattern = /\*\*([^*]+)\*\*/g
  let last = 0
  let match: RegExpExecArray | null
  while ((match = pattern.exec(raw))) {
    if (match.index > last) parts.push({ em: false, text: raw.slice(last, match.index) })
    parts.push({ em: true, text: match[1] ?? '' })
    last = match.index + match[0].length
  }
  if (last < raw.length) parts.push({ em: false, text: raw.slice(last) })
  if (parts.length === 0) parts.push({ em: false, text: raw })
  return parts
})

const until = computed(() => {
  const end = new Date(lastCall.endsAt - 1)
  return t('missions.lastCallUntil', {
    date: end.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }),
  })
})

onMounted(() => {
  dialogEl.value?.showModal()
})

function onScrimClick(event: MouseEvent): void {
  if (event.target === dialogEl.value) emit('close')
}
</script>

<style scoped>
.scrim {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  border: 0;
  background: transparent;
  overflow: hidden;
  animation: last-call-in var(--md-motion-medium) var(--md-motion-standard);
}

.scrim::backdrop {
  background: rgba(4, 10, 22, 0.72);
  backdrop-filter: blur(10px);
}

.panel {
  display: flex;
  flex-direction: column;
  width: min(720px, 100%);
  max-height: min(86vh, 760px);
  border-radius: 16px;
  overflow: hidden;
  background:
    radial-gradient(120% 80% at 0% 100%, rgba(62, 198, 255, 0.16), transparent 46%),
    radial-gradient(90% 70% at 100% 0%, rgba(226, 179, 64, 0.2), transparent 42%),
    #071422;
  border: 1px solid rgba(226, 179, 64, 0.55);
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.45),
    inset 0 0 0 1px rgba(215, 238, 248, 0.12);
  color: #f7fdff;
}

.hero {
  position: relative;
  flex: 0 0 auto;
  min-height: 168px;
  overflow: hidden;
}

.hero-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 42%;
}

.hero-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(7, 20, 34, 0.15) 0%, rgba(7, 20, 34, 0.82) 100%),
    linear-gradient(90deg, rgba(7, 20, 34, 0.55), transparent 55%);
}

.close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  background: rgba(7, 20, 34, 0.55);
  color: #f7fdff;
}

.hero-copy {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 168px;
  padding: 20px 24px 16px;
}

.kicker {
  margin: 0 0 4px;
  color: #e2b340;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 32px;
  font-weight: 500;
  line-height: 1.15;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.45);
}

.until {
  margin: 6px 0 0;
  color: #d7eef8;
  font-size: 13px;
}

.body {
  overflow: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px 8px;
}

.lead,
.foil,
.rules {
  margin: 0;
  color: #d7eef8;
  font-size: 14px;
  line-height: 20px;
}

.lead strong {
  color: #e2b340;
  font-weight: 700;
}

.rewards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(112px, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.reward {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 10px 8px 12px;
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(215, 238, 248, 0.12), rgba(14, 58, 102, 0.35));
  border: 1px solid rgba(62, 198, 255, 0.28);
  text-align: center;
}

.shot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 118px;
}

.shot.frame {
  height: 156px;
  overflow: visible;
}

.shot img {
  display: block;
  max-width: 100%;
  max-height: 112px;
  object-fit: contain;
  filter: drop-shadow(0 8px 12px rgba(0, 0, 0, 0.45));
}

.shot.sleeve img {
  height: 112px;
  width: auto;
}

.shot.mat img {
  width: 100%;
  height: 78px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(226, 179, 64, 0.45);
}

.pack-shot {
  position: relative;
  width: 78px;
  height: 112px;
}

.pack-shot .pack {
  width: 72px;
  height: auto;
  border-radius: 4px;
}

.pack-shot .card {
  position: absolute;
  right: -14px;
  bottom: 0;
  width: 46px;
  height: auto;
  border-radius: 3px;
  transform: rotate(8deg);
}

.at {
  margin: 0;
  color: #3ec6ff;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
}

.name {
  margin: 0;
  color: #f7fdff;
  font-size: 12px;
  line-height: 16px;
}

.meter progress {
  display: block;
  width: 100%;
  height: 8px;
  border: 0;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(215, 238, 248, 0.16);
  appearance: none;
}

.meter progress::-webkit-progress-bar {
  background: rgba(215, 238, 248, 0.16);
  border-radius: 999px;
}

.meter progress::-webkit-progress-value {
  border-radius: 999px;
  background: linear-gradient(90deg, #3ec6ff, #e2b340);
}

.meter progress::-moz-progress-bar {
  border-radius: 999px;
  background: linear-gradient(90deg, #3ec6ff, #e2b340);
}

.meter p {
  margin: 6px 0 0;
  color: #d7eef8;
  font-size: 13px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  padding: 8px 16px 16px;
}

.snooze {
  margin-right: auto;
}

.cta,
.ghost {
  min-height: 40px;
  padding: 8px 16px;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
}

.cta {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(180deg, #f0cb6a, #e2b340);
  color: #071422;
}

.ghost {
  border: 0;
  background: transparent;
  color: #d7eef8;
}

.ghost:hover,
.cta:hover {
  filter: brightness(1.08);
}

@keyframes last-call-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (max-width: 640px) {
  h2 {
    font-size: 26px;
  }

  .rewards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (orientation: landscape) and (max-height: 520px) {
  .hero,
  .hero-copy {
    min-height: 0;
  }

  .hero-art,
  .hero-shade {
    display: none;
  }

  .hero-copy {
    padding: 10px 52px 8px 16px;
  }

  h2 {
    font-size: 22px;
  }

  .until {
    margin-top: 2px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .scrim {
    animation: none;
  }

  .scrim::backdrop {
    backdrop-filter: none;
  }
}
</style>
